'use client';

import { DateTime } from 'luxon';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CategoryTypeById, CategoryTypeString } from '@diritto/common/content/constants';
import { ContestId } from '@diritto/common/content/contest.constants';
import { FancySharePublicDto } from '@diritto/common/fancyShare/dtos/fancy-share.dtos';
import { SectionDesign, SectionPosition } from '@diritto/common/home/constants/section-constants';
import { ImageTransformOptions, ImageTransformType } from '@diritto/common/imageKit/constants';
import { ContentRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodeRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { EpisodeReplyRelayDto } from '@diritto/common/relay/dtos/episode-reply-relay.dto';
import { ProfileLiteRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { RollingRelayDto } from '@diritto/common/relay/dtos/rolling-relay.dtos';
import { SectionRelayDto, SectionRollingDto } from '@diritto/common/relay/dtos/section-relay.dtos';
import { TrackerExtraInfo, TrackerPosition, TrackingEventType } from '@diritto/common/tracker/tracking-event.constant';
import { shuffle } from '@diritto/common/utils/array';
import { ProfileImage } from '@/app-components/accounts/profiles/ProfileImage';
import { SectionSlideButton } from '@/app-components/home/sections/components/SectionSlideButton';
import { SectionReply } from '@/app-components/reply/SectionReply';
import { RollingItem } from '@/app-components/rolling/RollingItem';
import Explore from '@/app/(appHeader)/promotions/2026-pick-and-choo/vote/components/Explore';
import { RightArrowIcon } from '@/app/(appHeader)/promotions/2026-pick-and-choo/vote/components/RightArrow';
import { VoteDatePill } from '@/app/(appHeader)/promotions/play-pick-and-choo/VoteDatePill';
import { useActiveContentContestIdsDispatch } from '@/contexts/ActiveContentContestIdsProvider';
import { useTrackingState } from '@/contexts/TrackingProvider';
import { useGetContestRepliesQuery } from '@/queries/contests/useGetContestCommentsQuery';
import { useGetContestFancySharesQuery } from '@/queries/contests/useGetContestFancySharesQuery';
import { useGetSectionsQuery } from '@/queries/relay/useGetSectionsQuery';
import { IkImage } from '@/ui-components/Imagekit/IkImage';
import { DotDivider } from '@/ui-components/common/DotDivider';
import { Link } from '@/ui-components/common/Link';
import { ContentBadgeGroup } from '@/ui-components/contents/ContentBadgeGroup';
import { BadgeMode } from '@/constants/badge';
import { IconCount, IconCountType } from '@/ui-components/contents/IconCount';
import { useSlide } from '@/ui-hooks/useSlide';
import { useAuthState } from 'contexts/AuthProvider';
import { useGetContestVoteContentsQuery } from 'queries/contests/useGetContestVoteContentsQuery';
import { useGetContestVotePeriodQuery } from 'queries/contests/useGetContestVotePeriodQuery';
import { configure } from 'utils/configure';

const CONTEST_ID = ContestId.Contest2026;

export const ContentTab = () => {
  const searchParams = useSearchParams();
  const target = searchParams?.get('target');

  const today = useMemo(() => {
    const now = DateTime.now().setZone('Asia/Seoul');
    return now.toFormat('yyyy-MM-dd');
  }, []);

  useEffect(() => {
    if (target === 'rollings') {
      document.getElementById('rollings')?.scrollIntoView();
      setTimeout(() => {
        window.history.replaceState(null, '', `?tab=content`);
      }, 1000);
    }
  }, [target]);

  return (
    <div className="relative w-full h-auto min-h-[100vh] overflow-hidden">
      <div className="flex flex-col w-full">
        <MyVotes className="flex flex-col w-full items-center !min-h-[622px]" currentYyyymmdd={today}>
          <div className="w-full h-[129px] flex flex-col items-center">
            <img
              src={`${configure.staticPrefix}/images/contests/4/vote/content/title/bg.png`}
              alt="content_1"
              className="w-full h-[129px] object-fill"
            />
            <div className="absolute top-20 w-full flex flex-col items-center">
              <img
                alt="내가 투표한 작품"
                src={`${configure.staticPrefix}/images/contests/4/vote/content/title/1.png`}
                style={{ height: `23px` }}
              />
            </div>
          </div>
        </MyVotes>
        <div id="rollings" className="scroll-mt-[72px]" />
        <Rollings className="w-full h-auto py-20 bg-[#FFDB59] flex-col items-center gap-6 inline-flex min-h-[1203px]">
          <img
            alt="작품 소개 읽고 투표하기"
            src={`${configure.staticPrefix}/images/contests/4/vote/content/title/2.png`}
            style={{ height: `23px` }}
          />
        </Rollings>
        <UserReactions className="w-full h-auto py-20 bg-gray-200 flex-col items-center gap-8 inline-flex min-h-[943px]">
          <img
            alt="독자들의 실시간 리액션"
            src={`${configure.staticPrefix}/images/contests/4/vote/content/title/3.png`}
            style={{ height: `23px` }}
          />
        </UserReactions>
        <div id="explore" className="scroll-mt-[72px]" />
        <Contents className="relative w-full bg-white flex-col items-center inline-flex mb-20">
          <div className="w-full h-[129px] flex flex-col items-center">
            <img
              src={`${configure.staticPrefix}/images/contests/4/vote/content/title/bg.png`}
              alt="content_1"
              className="w-full h-[129px] object-fill"
            />
            <div className="absolute top-20 w-full flex flex-col items-center">
              <img
                alt="장르별로 무제한 탐색하기"
                src={`${configure.staticPrefix}/images/contests/4/vote/content/title/4.png`}
                style={{ height: `23px` }}
              />
            </div>
          </div>
        </Contents>
      </div>
    </div>
  );
};

export const Effect = ({ className }: { className?: string }) => (
  <div
    className={`${className} absolute inset-0 bg-cover bg-center`}
    style={{
      backgroundImage: `url(${configure.staticPrefix}/images/contests/4/vote/content/title/effect.png)`,
      backgroundSize: '70%',
      backgroundPosition: 'center',
      backgroundRepeat: 'repeat',
      mixBlendMode: 'overlay',
      pointerEvents: 'none',
    }}
  />
);

const Height = 259;
const HorizontalGap = 8;

export default function MyVotes({
  className,
  children,
  currentYyyymmdd,
}: {
  className: string;
  children: React.ReactNode;
  currentYyyymmdd: string;
}) {
  const { isLoggedIn } = useAuthState();
  const { checkActiveContentContests } = useActiveContentContestIdsDispatch();
  const { data, isFetched } = useGetContestVoteContentsQuery(CONTEST_ID, isLoggedIn);
  const { data: votePeriod } = useGetContestVotePeriodQuery(CONTEST_ID);

  const [voteDates, setVoteDates] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<string>(currentYyyymmdd);
  const buttonsRef = useRef<(HTMLButtonElement | null)[]>([]); // 각 버튼에 대한 참조를 저장하는 배열

  const scrollToCenter = useCallback((index: number) => {
    const container = buttonsRef.current[0]?.parentElement;
    const button = buttonsRef.current[index];
    if (container && button) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      const scrollLeft =
        container.scrollLeft +
        (buttonRect.left + buttonRect.width / 2) -
        (containerRect.left + containerRect.width / 2);

      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
    }
  }, []);

  useEffect(() => {
    if (data?.contents) {
      const contents = Object.values(data.contents).flatMap(it => Object.values(it));
      const contentContestIds = contents.map(it => it.contentContestId).filter((it): it is number => !!it);
      checkActiveContentContests(ContestId.Contest2026, contentContestIds);
    }
  }, [data?.contents, checkActiveContentContests]);

  useEffect(() => {
    if (votePeriod) {
      const start = DateTime.fromJSDate(new Date(votePeriod.startAt)).setZone('Asia/Seoul');
      const end = DateTime.fromJSDate(new Date(votePeriod.endAt)).setZone('Asia/Seoul');
      const today = DateTime.fromJSDate(new Date(currentYyyymmdd)).setZone('Asia/Seoul');
      const dates = Array.from({ length: end.diff(start, 'days').days + 1 }, (_, i) =>
        start.plus({ days: i }).toFormat('yyyy-MM-dd'),
      ).slice(0, 7);
      setVoteDates(dates);
      if (dates.indexOf(today.toFormat('yyyy-MM-dd')) === -1) {
        setSelectedDate(dates[dates.length - 1]);
      } else {
        setSelectedDate(today.toFormat('yyyy-MM-dd'));
      }
    }
  }, [votePeriod, currentYyyymmdd]);

  const handleVoteDateClick = useCallback(
    (voteDate: string) => {
      setSelectedDate(voteDate);
      const index = voteDates.indexOf(voteDate);
      scrollToCenter(index);
    },
    [scrollToCenter, voteDates],
  );

  useEffect(() => {
    handleVoteDateClick(selectedDate);
  }, [handleVoteDateClick, selectedDate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const renderContent = useCallback(
    () =>
      Object.keys(data?.contents[selectedDate] ?? {}).length > 0 && (
        <div className="flex flex-col justify-center gap-[24px] items-center w-full">
          <div className="flex flex-row gap-2 px-4 md:px-0 max-w-[100vw] md:max-w-[590px] box-border overflow-x-auto overflow-y-hidden whitespace-nowrap [&::-webkit-scrollbar]:hidden relative">
            {Object.values(data?.contents[selectedDate] ?? []).map(content => {
              const profile = data?.profileLites[content.profileId];
              return (
                <div className={`w-[110px] h-[${Height}px] flex flex-col`} key={content.id}>
                  <Link href={`/contents/${content.id}`}>
                    <div className={`relative flex w-[110px] h-[165px] rounded bg-white overflow-hidden max-w-full`}>
                      <IkImage
                        transform={{
                          path: content.cover.path,
                          isPrivate: content.cover.isPrivate,
                          transformation: ImageTransformOptions[ImageTransformType.ContentCover],
                        }}
                        className="!flex !w-full !h-full object-cover object-center"
                        alt={`${content.title} 표지`}
                      />
                      <ContentBadgeGroup content={content} mode={BadgeMode.COVER} />
                    </div>
                  </Link>
                  <div className="flex flex-col w-full mt-2 gap-1">
                    <Link href={`/contents/${content.id}`}>
                      <div className="text-wrap w-full font-bold text-xxs line-clamp-2 leading-[16px]">
                        {content.title}
                      </div>
                    </Link>
                    <div className="flex flex-row gap-1 items-center font-medium text-xxxs text-gray-600">
                      <div className="block text-ellipsis line-clamp-1 break-all break-words">
                        <Link href={`/accounts/profiles/${profile?.id}`}>{profile?.nickname}</Link>
                      </div>
                      <DotDivider />
                      <div>{CategoryTypeString[CategoryTypeById[content.categoryId]]}</div>
                    </div>
                    <div className="flex flex-row gap-1 items-center font-medium text-xxxs text-gray-600">
                      <IconCount count={data?.viewCounts[content.id] || 0} type={IconCountType.View} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <button
            className="flex flex-wrap flex-none max-w-[306px] w-full h-[63px] justify-center items-center rounded-[6px] bg-black text-white"
            onClick={() => document.getElementById('explore')?.scrollIntoView()}>
            픽앤츄 작품 탐색하기
            <RightArrowIcon className="w-[6px] h-[9px] ml-2" color="white" />
          </button>
        </div>
      ),
    [data?.contents, data?.profileLites, data?.viewCounts, selectedDate],
  );

  return (
    <div className={className}>
      {children}
      <div className="flex flex-col justify-center gap-4 pb-20 w-full">
        {votePeriod && (
          <div className="flex flex-col justify-center items-center w-full">
            <div className="flex flex-row gap-1.5 px-4 max-w-[100vw] md:max-w-[545px] box-border overflow-x-auto overflow-y-hidden whitespace-nowrap [&::-webkit-scrollbar]:hidden relative">
              {voteDates.map((voteDate, index) => (
                <VoteDatePill
                  key={voteDate}
                  voteDate={voteDate}
                  selected={voteDate === selectedDate}
                  handleClick={handleVoteDateClick}
                  ref={el => {
                    buttonsRef.current[index] = el;
                  }}
                />
              ))}
            </div>
          </div>
        )}
        <div className={`flex justify-center flex-row px-4 gap-1.5 [&::-webkit-scrollbar]:hidden`}>
          {renderContent()}
          {isFetched && isLoggedIn && Object.keys(data?.contents[selectedDate] ?? {}).length === 0 && (
            <div className="flex flex-col justify-center gap-6 items-center w-full">
              <div className="flex flex-none h-[259px] flex-col gap-2 items-center justify-center text-[#6e7781] text-xxs">
                <img
                  src={`${configure.staticPrefix}/images/contests/4/vote/content/empty/ritto-finder.png`}
                  alt="empty"
                  className="size-[120px] object-fill"
                />
                <center>
                  투표한 작품이 없어요.
                  <br />
                  1일 최대 5작품까지 투표할 수 있어요.
                </center>
              </div>
              <button
                className="flex flex-wrap flex-none max-w-[306px] w-full h-[63px] justify-center items-center rounded-[6px] bg-black text-white"
                onClick={() => document.getElementById('explore')?.scrollIntoView()}>
                픽앤츄 작품 탐색하기
                <RightArrowIcon className="w-[6px] h-[9px] ml-2" color="white" />
              </button>
            </div>
          )}
          {!isLoggedIn && (
            <div className="flex flex-col justify-center gap-6 items-center w-full">
              <div className="flex flex-none h-[259px] flex-col gap-2 items-center justify-center text-[#6e7781] text-xxs">
                <img
                  src={`${configure.staticPrefix}/images/contests/4/vote/content/empty/ritto-wave.png`}
                  alt="empty"
                  className="size-[120px] object-fill"
                />
                <center>
                  디리토에 로그인 후
                  <br />
                  픽앤츄 작품에 투표해보세요!
                </center>
              </div>
              <button
                className="flex flex-wrap flex-none max-w-[306px] w-full h-[63px] justify-center items-center rounded-[6px] bg-black text-white"
                onClick={() => {
                  window.location.href = '/accounts/sign-in?returnUrl=%2Fpromotions%2F2026-pick-and-choo%2Fvote';
                }}>
                로그인하고 투표하기
                <RightArrowIcon className="w-[6px] h-[9px] ml-2" color="white" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export const Rollings = ({ className, children }: { className: string; children: React.ReactNode }) => {
  const { data: sectionResult } = useGetSectionsQuery({
    includeAdult: true,
    position: SectionPosition.Contest,
  });
  const sections = sectionResult?.sections.filter(it => it.design === SectionDesign.Rolling) as SectionRollingDto[];

  return (
    <div className={`${className} flex flex-col gap-6 h-auto relative`}>
      <Effect />
      {children}
      {sections?.map(it => (
        <div className="flex flex-col gap-4 w-full" key={it.slug}>
          <div className="flex flex-row justify-between items-start px-4">
            <div className="text-sm font-medium leading-[27px]">{it.title}</div>
            <Link
              className="flex-none flex items-center text-white font-medium text-xxs leading-[27px]"
              href={it.moreLink}
              onClick={() => {}}>
              더보기
            </Link>
          </div>
          <RollingList
            section={it}
            rollingIds={it.rollingIds}
            rollings={sectionResult?.rollings ?? {}}
            profiles={sectionResult?.profileLites || {}}
          />
        </div>
      ))}
    </div>
  );
};

function UserReactions({ className, children }: { className: string; children: React.ReactNode }) {
  const { data: fancySharesData } = useGetContestFancySharesQuery(CONTEST_ID);
  const { data: repliesData } = useGetContestRepliesQuery(CONTEST_ID);

  return (
    <div className={`${className} flex flex-col gap-6 h-auto relative`}>
      <Effect />
      {children}
      {fancySharesData && (
        <div className="flex flex-col gap-4 w-full" key="fancy-shares">
          <div className="flex flex-row justify-between items-start px-4">
            <div className="text-sm font-bold leading-[27px]">실시간 이멋공</div>
            {fancySharesData.fancyShares.length > 0 && (
              <Link
                className="flex-none flex items-center text-gray-500 font-medium text-xxs leading-[27px]"
                href={`/sections/contests/${CONTEST_ID}/fancy-share`}
                onClick={() => {}}>
                더보기
              </Link>
            )}
          </div>
          {fancySharesData.fancyShares.length === 0 ? (
            <EmptyList description="아직 남겨진 이멋공이 없어요." className="min-h-[378px]" />
          ) : (
            <FancyShareList fancyShares={fancySharesData.fancyShares} profiles={fancySharesData.profiles} />
          )}
        </div>
      )}
      {repliesData && (
        <div className="flex flex-col gap-4 w-full" key="replies">
          <div className="flex flex-row justify-between items-start px-4">
            <div className="text-sm font-bold leading-[27px]">실시간 댓글</div>
            {repliesData.replies.length > 0 && (
              <Link
                className="flex-none flex items-center text-gray-500 font-medium text-xxs leading-[27px]"
                href={`/sections/contests/${CONTEST_ID}/live-replies`}
                onClick={() => {}}>
                더보기
              </Link>
            )}
          </div>
          {repliesData.replies.length === 0 ? (
            <EmptyList description="아직 남겨진 댓글이 없어요." className="min-h-[232px]" />
          ) : (
            <ReplyList
              replies={repliesData.replies}
              profiles={repliesData.profileLites}
              contents={repliesData.contents}
              episodes={repliesData.episodes}
            />
          )}
        </div>
      )}
    </div>
  );
}
function Contents({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      {children}
      <Explore contestId={CONTEST_ID} />
    </div>
  );
}

export function FancyShareList({
  fancyShares,
  profiles,
}: {
  fancyShares: FancySharePublicDto[];
  profiles: Record<number, ProfileLiteRelayDto>;
}) {
  const { tracker } = useTrackingState();
  const trackerExtraInfo: TrackerExtraInfo = { trackerPosition: TrackerPosition.Contest };
  const slideRef = useRef<HTMLDivElement>(null);
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  useEffect(() => {
    tracker.track(TrackingEventType.ViewFancyShareList, { trackerExtraInfo });
  }, [tracker, trackerExtraInfo]);

  return (
    <div className="block relative min-h-[378px] [&_*::-webkit-scrollbar]:hidden">
      <div className="flex absolute w-full h-full left-[10px]" style={{ width: 'calc(100% - 20px)' }}>
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
      </div>

      <div
        className={`relative flex flex-col content-start flex-wrap px-4 overflow-x-scroll md:overflow-x-hidden gap-2 md:px-0 md:mx-[15px] h-[378px]`}
        ref={slideRef}
        onScroll={handleSlide}>
        {fancyShares.map(fancyShare => {
          const profile = profiles[fancyShare.profileId];

          return (
            <FancyShareItem
              key={fancyShare.id}
              fancyShare={fancyShare}
              profile={profile}
              trackerExtraInfo={trackerExtraInfo}
              onClick={() => {}}
            />
          );
        })}
      </div>
    </div>
  );
}

function FancyShareItem({
  fancyShare,
  onClick,
  profile,
}: {
  fancyShare: FancySharePublicDto;
  trackerExtraInfo: TrackerExtraInfo;
  onClick: () => void;
  profile: ProfileLiteRelayDto;
}) {
  const link = `/fancy-shares/${fancyShare.id}`;

  return (
    <div className="w-[346px] h-[378px] flex flex-col gap-2" onClick={onClick}>
      <Link href={link} className="flex items-center w-full">
        <div className="w-full flex bg-gray-200">
          <IkImage
            className="rounded aspect-square"
            transform={{
              path: fancyShare.file.path,
              isPrivate: fancyShare.file.isPrivate,
              transformation: ImageTransformOptions[ImageTransformType.FancyShare],
            }}
            alt={fancyShare.description}
            fullWidth
          />
        </div>
      </Link>
      {profile && !profile.isDeleted && (
        <Link href={`/accounts/profiles/${profile.id}`} className="flex flex-row flex-nowrap items-center gap-2">
          <div className="flex-shrink-0">
            <ProfileImage
              file={'profileImage' in profile ? profile.profileImage : undefined}
              defaultImageKey={profile.nickname}
              size={24}
            />
          </div>
          <div className="flex-shrink text-black text-xxs font-medium truncate">{profile.nickname}</div>
        </Link>
      )}
    </div>
  );
}

const ReplyList = ({
  replies,
  profiles,
  contents,
  episodes,
}: {
  replies: EpisodeReplyRelayDto[];
  profiles: Record<number, ProfileLiteRelayDto>;
  contents: Record<number, ContentRelayDto>;
  episodes: Record<number, EpisodeRelayDto>;
}) => {
  const { tracker } = useTrackingState();
  const trackerExtraInfo: TrackerExtraInfo = { trackerPosition: TrackerPosition.Contest };
  const slideRef = useRef<HTMLDivElement>(null);
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);

  useEffect(() => {
    tracker.track(TrackingEventType.ViewFancyShareList, { trackerExtraInfo });
  }, [tracker, trackerExtraInfo]);

  return (
    <div className="block relative min-h-[232px] [&_*::-webkit-scrollbar]:hidden">
      <div className="flex absolute w-full h-full left-[10px]" style={{ width: 'calc(100% - 20px)' }}>
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
      </div>
      <div
        className={`relative flex flex-col content-start flex-wrap px-4 overflow-x-scroll md:overflow-x-hidden gap-2 md:px-0 md:mx-[15px] h-[232px]`}
        ref={slideRef}
        onScroll={handleSlide}>
        {replies.map(reply => (
          <SectionReply
            className="pc:!w-[169px] !w-[160px] !h-[232px] !bg-white"
            key={reply.id}
            reply={reply as EpisodeReplyRelayDto}
            contents={contents}
            episodes={episodes}
            profileLites={profiles}
            descriptionLineCount={4}
            trackerExtraInfo={trackerExtraInfo}
          />
        ))}
      </div>
    </div>
  );
};

const RollingList = ({
  section,
  rollingIds,
  rollings,
  profiles,
}: {
  section: SectionRelayDto;
  rollingIds: number[];
  profiles: Record<number, ProfileLiteRelayDto>;
  rollings: Record<number, RollingRelayDto>;
}) => {
  const { sessionSeed } = useAuthState();
  const { tracker } = useTrackingState();
  const slideRef = useRef<HTMLDivElement>(null);
  const { handlePrevSlide, handleNextSlide, handleSlide, showSlideButton } = useSlide(slideRef, HorizontalGap);
  const randomRollingIds = useMemo(() => shuffle(rollingIds, sessionSeed), [rollingIds, sessionSeed]);
  const trackerExtraInfo = {
    trackerPosition: TrackerPosition.Contest,
  };
  return (
    <div className="block relative min-h-[188px] [&_*::-webkit-scrollbar]:hidden">
      <div className="flex absolute w-full h-full left-[10px]" style={{ width: 'calc(100% - 20px)' }}>
        <SectionSlideButton
          showSlideButton={showSlideButton}
          handleNextSlide={handleNextSlide}
          handlePrevSlide={handlePrevSlide}
        />
      </div>
      <div
        className={`relative flex flex-col flex-wrap content-start px-4 overflow-x-scroll md:overflow-x-hidden gap-2 md:px-0 md:mx-[15px] h-[188px]`}
        ref={slideRef}
        onScroll={handleSlide}>
        {randomRollingIds.map((rollingId, index) => {
          const rolling = rollings[rollingId];
          if (!rolling) {
            return null;
          }

          const profile = profiles[rolling.profileId];
          if (!profile || profile.isDeleted) {
            return null;
          }

          return (
            <RollingItem
              key={rolling.id}
              className="flex !w-[228px] h-[188px] bg-white border !border-white border-solid shadow-[1px_-1px_1px_-1px_rgba(0,0,0,0.06)]"
              rolling={rolling}
              profile={profile}
              trackerExtraInfo={trackerExtraInfo}
              showMoreMenu={false}
              onClick={() => {
                tracker.track(TrackingEventType.ClickItemOnHomeSection, {
                  sectionId: section.id,
                  sectionDesign: section.design,
                  listItemIndex: index,
                  trackerExtraInfo,
                });
              }}
              showDescLines={4}
            />
          );
        })}
      </div>
    </div>
  );
};

const EmptyList = ({ description, className }: { description: string; className?: string }) => (
  <div
    className={`flex flex-col flex-1 gap-2.5 w-full justify-center items-center text-gray-600 text-xxs font-medium leading-[21px] ${className}`}>
    <img
      src={`${configure.staticPrefix}/images/contests/4/vote/content/empty/ritto-sad.png`}
      alt="empty"
      className="size-[120px] object-fill"
    />
    <center>{description}</center>
  </div>
);
